import PropTypes from "prop-types";
import React, { useRef, useState } from "react";

import { window } from "@swa-ui/browser";
import { classNames } from "@swa-ui/string";

import globalRules from "../assets/styles/globalRules.module.scss";
import { Button } from "../Button";
import { keyCodes } from "../defines/keyCodes";
import { Digit } from "../Digit";
import { FadingDot } from "../FadingDot";
import { Icon } from "../Icon";
import styles from "./NumberSelector.module.scss";

// TODO: Refactor NumberSelector to use font size design tokens - PHX-2658
const DECREMENT = -1;
const DOT_ANIMATION_DURATION = undefined;
const DOT_ANIMATION_DURATION_IMMEDIATE = 0.0001;
const INCREMENT = 1;
const LARGE_FONT_SIZE = 32;
const MEDIUM_FONT_SIZE = 30;
const NUMBER_SELECTOR_FEEDBACK_DESIGN_TOKEN = "cmp-core-color-number-selector-feedback";
const NUMBER_SELECTOR_ICON_DISABLED_DESIGN_TOKEN = "cmp-core-color-number-selector-icon-disabled";
const NUMBER_SELECTOR_ICON_FG_DESIGN_TOKEN = "cmp-core-color-number-selector-icon-fg";
const OFF = 0;
const ON = 1;
const SMALL_FONT_SIZE = 24;

/**
 * A component that allows the user to enter a numeric value by incrementing / decrementing the value
 * using plus and minus (+ / -) buttons. Only single digits are supported: 0-9.
 */

export const NumberSelector = React.forwardRef((props, ref) => {
  const {
    "aria-label": ariaLabel = "quantity",
    className,
    defaultValue = 0,
    disableDecrement = false,
    disableIncrement = false,
    id,
    maximumValue = 9,
    minimumValue = 0,
    name,
    onBlur,
    onChange,
    onFocus,
    size = "large",
    value,
  } = props;
  const [focusedButton, setFocusedButton] = useState(OFF);
  const [hover, setHover] = useState(false);
  const [on, setOn] = useState(OFF);
  const containerRef = ref || useRef();
  const decrementRef = useRef();
  const incrementRef = useRef();

  return (
    <div {...getProps()}>
      <Button {...getButtonProps(DECREMENT)}>
        <FadingDot {...getDotProps(DECREMENT)} />
        <Icon {...getIconProps(DECREMENT)} />
      </Button>
      <Digit {...getDigitProps()} />
      <span aria-live="assertive" className={globalRules.hiddenFromScreen}>
        {`${props["aria-label-value"]} ${getValue()}`}
      </span>
      <Button {...getButtonProps(INCREMENT)}>
        <FadingDot {...getDotProps(INCREMENT)} />
        <Icon {...getIconProps(INCREMENT)} />
      </Button>
    </div>
  );

  function getProps() {
    return {
      "aria-label": ariaLabel,
      "aria-labelledby": name,
      "aria-valuemax": maximumValue,
      "aria-valuemin": minimumValue,
      "aria-valuenow": value,
      className: classNames(className, styles.numberSelectorContainer, {
        [styles.small]: size === "small",
      }),
      id,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      onMouseEnter: () => setHover(true),
      onMouseLeave: () => setHover(false),
      ref: containerRef,
      role: "spinbutton",
      tabIndex: 0,
    };
  }

  function getButtonProps(direction) {
    return {
      animate: false,
      "aria-label": props[isDecrement(direction) ? "aria-label-decrement" : "aria-label-increment"],
      className: getButtonClass(direction),
      clickFeedback: "none",
      defaultValue,
      disabled: isButtonDisabled(direction),
      name,
      onClick: () => handleClick(direction),
      onFocus: () => containerRef.current.focus(),
      ref: isDecrement(direction) ? decrementRef : incrementRef,
      styleType: "no-style",
      tabIndex: -1,
    };
  }

  function getDotProps(direction) {
    return {
      className: classNames(styles.feedback, { [styles.none]: on !== OFF }),
      color: NUMBER_SELECTOR_FEEDBACK_DESIGN_TOKEN,
      duration: on === direction ? DOT_ANIMATION_DURATION : DOT_ANIMATION_DURATION_IMMEDIATE,
      fullWidth: true,
      onTransformationEnd: on === direction ? handleTransformationEnd : undefined,
      to: on === direction ? "FADE-GROW" : "SHOW-SHRINK",
    };
  }

  function getIconProps(direction) {
    const isDisabled = isButtonDisabled(direction);

    return {
      className: getIconClass(direction),
      color: isDisabled
        ? NUMBER_SELECTOR_ICON_DISABLED_DESIGN_TOKEN
        : NUMBER_SELECTOR_ICON_FG_DESIGN_TOKEN,
      name: isDecrement(direction) ? "Minus" : "Plus",
      size: size === "large" ? "size20" : "size14",
      transparentBorder: true,
    };
  }

  function getDigitProps() {
    return {
      className: getDigitClass(),
      fontSize: getFontSize(),
      maxNumber: maximumValue,
      number: getValue(),
    };
  }

  function getButtonClass(direction) {
    return classNames(styles.numberSelectorButton, {
      [styles.disabled]: isButtonDisabled(direction),
      [styles.focus]: focusedButton !== OFF,
      [styles.hover]: hover,
    });
  }

  function getIconClass(direction) {
    const isDisabled = isButtonDisabled(direction);

    return classNames(styles.icon, {
      [styles.disabled]: isDisabled,
      [styles.small]: size === "small",
    });
  }

  function getDigitClass() {
    return classNames(styles.numberCount, {
      [styles.disabled]: disableDecrement && disableIncrement,
      [styles.small]: size === "small",
    });
  }

  function handleBlur(event) {
    setFocusedButton(OFF);
    onBlur?.(event);
  }

  function handleFocus(event) {
    setFocusedButton(ON);
    onFocus?.(event);
  }

  function handleKeyDown(event) {
    const { key } = event;

    if (key === keyCodes.KEY_END || key === keyCodes.KEY_PAGE_DOWN) {
      processEnd();
    } else if (key === keyCodes.KEY_HOME || key === keyCodes.KEY_PAGE_UP) {
      processHome();
    } else if (key === keyCodes.KEY_DOWN || key === keyCodes.KEY_LEFT || key === "-") {
      processDecrement(event);
    } else if (key === keyCodes.KEY_UP || key === keyCodes.KEY_RIGHT || key === "+") {
      processIncrement(event);
    } else if (key >= "0" && key <= `${maximumValue}`) {
      processDigit(key);
    }
  }

  function processEnd() {
    onChange(maximumValue);
    setOn(INCREMENT);
  }

  function processHome() {
    onChange(minimumValue);
    setOn(DECREMENT);
  }

  function processDecrement(event) {
    event.preventDefault();
    event.stopPropagation();

    if (getValue() > minimumValue) {
      updateValue(DECREMENT);
    }
  }

  function processIncrement(event) {
    event.preventDefault();
    event.stopPropagation();

    if (getValue() < maximumValue) {
      updateValue(INCREMENT);
    }
  }

  function processDigit(key) {
    const newValue = parseInt(key);

    if (newValue !== value) {
      onChange(newValue);
    }
  }

  function handleClick(direction) {
    updateValue(direction);
  }

  function handleTransformationEnd() {
    setOn(OFF);
  }

  function isButtonDisabled(direction) {
    const newValue = getValue();

    return isDecrement(direction)
      ? disableDecrement || newValue <= minimumValue
      : disableIncrement || newValue >= maximumValue;
  }

  function isDecrement(direction) {
    return direction === DECREMENT;
  }

  function updateValue(direction) {
    const newValue = getValue() + direction;

    onChange(newValue);
    setOn(direction);
  }

  function getValue() {
    return value ? value : defaultValue;
  }

  function getFontSize() {
    return parseInt(
      getCustomValue(size === "large" ? LARGE_FONT_SIZE : SMALL_FONT_SIZE, MEDIUM_FONT_SIZE)
    );
  }

  function getCustomValue(propertyName, customDefaultValue) {
    return (
      window.getComputedStyle(window.document.body).getPropertyValue(`--${propertyName}`) ||
      customDefaultValue
    );
  }
});

NumberSelector.propTypes = {
  /** aria-label text to provide accessibility description for NumberSelector. */
  "aria-label": PropTypes.string,

  /** aria-label text to provide accessibility description for decrement button. */
  "aria-label-decrement": PropTypes.string.isRequired,

  /** aria-label text to provide accessibility description for increment button. */
  "aria-label-increment": PropTypes.string.isRequired,

  /** aria-label text to be read as the value is decremented/incremented. */
  "aria-label-value": PropTypes.string.isRequired,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Optional initial default value for number selector. */
  defaultValue: PropTypes.number,

  /**
   * Indicates if the NumberSelector should accept events to decrement the value, and whether disabled
   * styling should be applied to the decrement button. If both decrement and increment buttons are
   * disabled, the digit will also show disabled styling.
   */
  disabledDecrement: PropTypes.bool,

  /**
   * Indicates if the NumberSelector should accept events to increment the value, and whether disabled
   * styling should be applied to the increment button. If both decrement and increment buttons are
   * disabled, the digit will also show disabled styling.
   */
  disabledIncrement: PropTypes.bool,

  /** Unique identifier which is passed to container element of NumberSelector. */
  id: PropTypes.string,

  /**
   * The maximum number that NumberSelector will allow. NumberSelector only allows for single
   * digits, so the maximum value cannot be greater than nine. Once reached, the increment button
   * will become disabled.
   */
  maximumValue: PropTypes.number,

  /**
   * The minimum number that the number selector will allow. Default is 0.
   * Once reached the decrement button will become disabled.
   */
  minimumValue: PropTypes.number,

  /** Name given to number selector when form is submitted. */
  name: PropTypes.string,

  /** A function that updates the value when a button "group" loses focus. */
  onBlur: PropTypes.func,

  /** A function that updates the value when a value change is made. */
  onChange: PropTypes.func.isRequired,

  /** A function that updates the value when a button "group" receives focus. */
  onFocus: PropTypes.func,

  /** Size to render buttons and digit. */
  size: PropTypes.oneOf(["large", "small"]),

  /** The value that will be displayed and modified. */
  value: PropTypes.number,
};

NumberSelector.displayName = "NumberSelector";
